import React, { useEffect, useState } from "react";
import Hero from "../components/Hero";
import NewsCard from "../components/NewsCard";
import { Link } from "react-router-dom";
import { ArrowRight, Trophy, Sparkles, Loader2 } from "lucide-react";
import { api } from "../lib/api";

const Section = ({ title, subtitle, action, children }) => (
  <section className="mx-auto max-w-7xl px-4 mt-16">
    <div className="flex items-end justify-between gap-4 mb-6">
      <div>
        <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#9d7bff]">{subtitle}</div>
        <h2 className="mt-1 text-2xl md:text-3xl font-bold text-white">{title}</h2>
      </div>
      {action}
    </div>
    {children}
  </section>
);

const Home = () => {
  const [articles, setArticles] = useState([]);
  const [events, setEvents] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [a, e, s] = await Promise.all([
          api.listArticles(),
          api.listEvents(),
          api.getStats(),
        ]);
        setArticles(a);
        setEvents(e);
        setStats(s);
      } catch (err) {
        console.error("Home load error", err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const latest = articles.slice(0, 12);

  const statCards = [
    { label: "Jahre Habbo", value: stats ? `${stats.years}+` : "20+" },
    { label: "Events", value: stats ? `${stats.events}` : "–" },
    { label: "Artikel", value: stats ? `${stats.articles}` : "–" },
    { label: "Rare Möbel", value: stats ? `${stats.rares}` : "–" },
  ];

  return (
    <div>
      <Hero />

      <section className="mx-auto max-w-7xl px-4">
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          {statCards.map((s) => (
            <div key={s.label} className="rounded-xl border border-white/10 bg-[#0f141b] p-4 text-center">
              <div className="pixel-text text-2xl text-white">{s.value}</div>
              <div className="mt-1 text-xs uppercase tracking-wider text-zinc-400">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      <Section
        subtitle="Aktuell"
        title="Neueste Artikel"
        action={
          <Link to="/news" className="inline-flex items-center gap-1 text-sm text-[#b39bff] hover:text-white">
            Alle ansehen <ArrowRight className="h-4 w-4" />
          </Link>
        }
      >
        {loading ? (
          <div className="flex items-center justify-center py-16 text-zinc-400">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {latest.map((a) => <NewsCard key={a.id} article={a} />)}
          </div>
        )}
      </Section>

      <Section
        subtitle="Höhepunkte"
        title="Events"
        action={
          <Link to="/events" className="inline-flex items-center gap-1 text-sm text-[#b39bff] hover:text-white">
            Alle Events <ArrowRight className="h-4 w-4" />
          </Link>
        }
      >
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
          {events.map((e) => {
            const firstBadge = (e.badges || [])[0];
            return (
              <Link
                key={e.id}
                to={`/events/${e.id}`}
                data-testid={`home-event-${e.id}`}
                className="card-hover rounded-xl border border-white/10 bg-[#0f141b] p-4 text-center hover:border-[#ffb84d]/40"
              >
                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-lg bg-[#10141b]">
                  {firstBadge ? (
                    <img src={`https://images.habbo.com/c_images/album1584/${firstBadge.code}.gif`} alt={firstBadge.code} className="hb-pixel-img h-12 w-12" />
                  ) : e.image ? (
                    <img src={e.image} alt={e.name} className="hb-pixel-img max-h-12" />
                  ) : (
                    <Trophy className="h-7 w-7 text-[#ffb84d]" />
                  )}
                </div>
                <div className="mt-3 text-xs text-[#9d7bff]">{e.year}</div>
                <div className="mt-1 text-sm font-semibold text-white line-clamp-2">{e.name}</div>
              </Link>
            );
          })}
        </div>
      </Section>

      <section className="mx-auto max-w-7xl px-4 mt-20">
        <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-[#171225] via-[#13182a] to-[#1a1110] p-8 md:p-12">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-6 justify-between">
            <div>
              <div className="inline-flex items-center gap-2 text-xs uppercase tracking-wider text-[#ffb84d]">
                <Sparkles className="h-4 w-4" /> Werde Teil der Habthek
              </div>
              <h3 className="mt-2 text-2xl md:text-3xl font-bold text-white">
                Du kennst eine Habbo Story, die hier fehlt?
              </h3>
              <p className="mt-2 max-w-xl text-sm text-zinc-300">
                Schreib uns deine Erinnerungen, alte Screenshots oder Glitches. Wir dokumentieren sie und nehmen dich in den Credits auf.
              </p>
            </div>
            <Link to="/meine-stories" className="btn-primary rounded-lg px-5 py-3 text-sm font-semibold inline-flex items-center gap-2" data-testid="home-submit-story">
              Story einreichen
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
